import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { navigate, Link, RouteComponentProps } from '@reach/router';
import { Helmet } from 'react-helmet';
import { appDispatch, RootState } from 'store/store';
import messages from './Messages';
import Button from 'components/Button';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ICompany, ICompanyState } from 'store/companies/companiesTypes';
import i18n, { languageDirection } from '../../../i18n';
import styled from 'styled-components';
import {
  absoluteBlack,
  black,
  exactWhite,
  orange,
} from '../../../utils/style/colors';
import Input from '../../../components/Input/Input';
import LocalIcon from '../../../ui/LocalIcon';
import APopover from '../../../components/APopover';
import { registerProvider } from '../../../store/providers/providerSlice';
import { IProvider } from '../../../store/providers/providerTypes';
import { getByManagerCompany } from '../../../api/ManApi';
import { isCompanyOwnerOrSupport } from '../../../store/companies/companiesUtils';
import { CircleContainer } from '../UserProfile/styles';

interface IStateToProps {
  thisUser: IThisUserState;
  companies: ICompanyState;
  rootState: RootState;
}

interface IComponentState {
  newProviderName: string;
  createNewProviderLoading: boolean;
  currentlyCreatedProviders: {
    [p: string | number]: IProvider[];
  };
}

type IComponentProps = IStateToProps & RouteComponentProps;

export const BadgeContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
`;

export const LineBadge = styled.div`
  border-inline-start: 2px solid ${orange};
  background: rgba(255, 165, 0, 0.1);
  padding: 0.5rem;
  flex-basis: 49%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;

  @media screen and (max-width: 800px) {
    flex-basis: 100%;
  }
`;

export const BadgeName = styled.div``;

export const MoreSetting = styled.div`
  display: flex;
  gap: 1rem;
`;

export const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const APopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: ${exactWhite};
`;

class CompaniesList extends React.Component<IComponentProps, IComponentState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      newProviderName: '',
      createNewProviderLoading: false,
      currentlyCreatedProviders: {},
    };
  }

  componentDidMount() {
    const providers = {};
    Object.values(this.props.companies.data).forEach(
      async (company: ICompany) => {
        const res = await getByManagerCompany(this.props.rootState, company.id);
        providers[company.id] = res.data;
        this.setState({
          currentlyCreatedProviders: providers,
        });
      }
    );
  }

  handleClickOnCreateButton = () => {
    navigate('/c/company/editor');
  };

  handleCreateNewProvider = (companyId: number) => {
    this.setState({ createNewProviderLoading: true });

    appDispatch(
      registerProvider({
        provider: {
          managingCompanyId: companyId,
          name: this.state.newProviderName,
        } as IProvider,
      })
    ).then((res: any) => {
      this.setState((prevState) => ({
        createNewProviderLoading: false,
        currentlyCreatedProviders: {
          ...prevState.currentlyCreatedProviders,
          [companyId]: [
            ...(prevState.currentlyCreatedProviders[companyId] || []),
            res.provider,
          ],
        },
      }));
    });
  };

  render() {
    const { companies, thisUser } = this.props;

    return (
      <div
        style={{ background: exactWhite, padding: '0 0.5rem', height: '100%' }}
      >
        <Helmet>
          <title>
            {i18n.t('companies', { ns: 'companyManagement' }) as string}
          </title>
          <meta name="description" content={messages.companies} />
        </Helmet>
        <div className="section" style={{ paddingInlineStart: '0.75rem' }}>
          <div className="flex justify-between">
            <Button color="green" onClick={this.handleClickOnCreateButton}>
              {
                i18n.t('createNewCompany', {
                  ns: 'companyManagement',
                }) as string
              }
            </Button>
            <div className="px-5">
              <CircleContainer>
                <LocalIcon
                  iconName={'back-square.svg'}
                  onClick={() => {
                    history.back();
                  }}
                  isLink={true}
                />
              </CircleContainer>
            </div>
          </div>
        </div>
        <div style={{ overflowY: 'auto' }}>
          <div className="row" style={{ overflow: 'hidden auto' }}>
            <div className="section">
              {Object.values(companies.data)
                .sort((a: ICompany, b: ICompany) => {
                  if (b.name < a.name) {
                    return 1;
                  }
                  return 0;
                })
                .map((company: ICompany) => {
                  const isOwnerOrSupport = isCompanyOwnerOrSupport(
                    thisUser,
                    company
                  );
                  return (
                    <div
                      key={company.id}
                      className="col s12"
                      style={{
                        float: languageDirection() === 'rtl' ? 'right' : 'left',
                        borderRadius: '1rem',
                        overflow: 'hidden',
                        color: absoluteBlack,
                      }}
                    >
                      <div
                        className="card"
                        style={{
                          borderRadius: '1rem',
                          overflow: 'hidden',
                          background: exactWhite,
                        }}
                      >
                        <div
                          className="card-content flex justify-between"
                          style={{ background: exactWhite }}
                        >
                          <span className="card-title text-ellipsis">
                            {company.name}
                          </span>
                          <span className="flex gap-5">
                            {Number(thisUser.userId) ===
                              company.owner.userId && (
                              <div>
                                <Link
                                  className="left"
                                  to={`/c/company/editor/${company.id}`}
                                >
                                  <LocalIcon
                                    iconName={'edit.svg'}
                                    color={orange}
                                    isLink={true}
                                    type={'sm'}
                                    title={i18n.t('editDS', {
                                      ns: 'companyManagement',
                                    })}
                                  />
                                </Link>
                              </div>
                            )}
                            {isOwnerOrSupport && (
                              <Link to={`/t/${company.id}/admin/project/list`}>
                                <LocalIcon
                                  iconName={'setting.svg'}
                                  color={orange}
                                  isLink={true}
                                  type={'sm'}
                                  title={i18n.t('companySetting', {
                                    ns: 'companyManagement',
                                  })}
                                />
                              </Link>
                            )}
                          </span>
                        </div>
                        {
                          <div className="font-bold px-4 flex items-center">
                            <div>
                              {
                                i18n.t('DS', {
                                  ns: 'companyManagement',
                                }) as string
                              }
                            </div>
                            {isOwnerOrSupport && (
                              <APopover
                                target={
                                  <div style={{ marginInlineStart: '0.5rem' }}>
                                    <LocalIcon
                                      iconName={'add-square.svg'}
                                      type={'sm'}
                                      color={orange}
                                      isLink={true}
                                      onClick={() => {
                                        /*console.log(e);*/
                                      }}
                                      title={i18n.t('addDS', {
                                        ns: 'companyManagement',
                                      })}
                                    />
                                  </div>
                                }
                                trigger={'click'}
                              >
                                <APopoverContainer>
                                  <Input
                                    label={
                                      i18n.t('newProviderName', {
                                        ns: 'companyManagement',
                                      }) as string
                                    }
                                    id={`new-provider-${Math.round(Math.random() * 1000)}`}
                                    type="text"
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                      this.setState({
                                        newProviderName: e.target.value,
                                      });
                                    }}
                                  />
                                  <Button
                                    onClick={() =>
                                      this.handleCreateNewProvider(company.id)
                                    }
                                    type={'button'}
                                    color={'black-outline'}
                                    loading={
                                      this.state.createNewProviderLoading
                                    }
                                  >
                                    {
                                      i18n.t('save', {
                                        ns: 'companyManagement',
                                      }) as string
                                    }
                                  </Button>
                                </APopoverContainer>
                              </APopover>
                            )}
                          </div>
                        }
                        <BadgeContainer>
                          {Object.keys(
                            this.state.currentlyCreatedProviders
                          ).includes(company.id.toString())
                            ? this.state.currentlyCreatedProviders[
                                company.id
                              ].map((provider: IProvider) => {
                                return (
                                  <LineBadge key={provider.id}>
                                    <BadgeName>{provider.name}</BadgeName>
                                    <MoreSetting>
                                      <ItemContainer>
                                        <Input
                                          key={provider.id}
                                          id={provider.id.toString()}
                                          type={'checkbox'}
                                          disabled={true}
                                          checked={provider.useVirtualSpaces}
                                        />
                                        <span>
                                          {
                                            i18n.t('virtualSpaceStatus', {
                                              ns: 'companyManagement',
                                            }) as string
                                          }
                                        </span>
                                      </ItemContainer>
                                      {isOwnerOrSupport && (
                                        <ItemContainer>
                                          <Link
                                            to={`/content/${provider.id}/settings`}
                                          >
                                            <LocalIcon
                                              iconName={'setting.svg'}
                                              color={black}
                                              isLink={true}
                                              onClick={() => {
                                                // console.log(e);
                                              }}
                                            />
                                          </Link>
                                        </ItemContainer>
                                      )}
                                    </MoreSetting>
                                  </LineBadge>
                                );
                              })
                            : null}
                        </BadgeContainer>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<
  IStateToProps,
  RouteComponentProps,
  RootState
> = (state: RootState) => {
  const companies = state.companies;
  const thisUser = state.thisUser;

  return { companies, thisUser, rootState: state };
};

export default connect(mapStateToProps)(CompaniesList);
